import { TextNode, } from "@bracket/ui/nodes";
import type { ColorDeclaration, FontWeight, SizeVariant, TypeVariant, VerticalAlignment, HorizontalAlignment, } from "@bracket/ui/types";
import type { CSSProperties, ReactNode, } from "react";
import type { ThemePalette, } from "@bracket/ui/types";
import * as accessors from "./accessors";

export declare type TextProps = {

	as?: TypeVariant | "span" | "a" | "div";
	className?: string;
	children: string | string[] | ReactNode;
	color?: keyof ThemePalette | ColorDeclaration;
	variant?: TypeVariant;
	title?: string;
	size?: SizeVariant;
	lineHeight?: string;
	opacity?: number;
	weight?: FontWeight;
	light?: boolean;
	dark?: boolean;
	frame?: boolean;
	align?: VerticalAlignment;
	justify?: HorizontalAlignment;
	numeric?: boolean;
	style?: CSSProperties;
	hide?: boolean;
	bold?: boolean;
	indent?: boolean|number;
	margin?: string;
	$flow?: "stream"|"stack"; // annoying AF - we need to determine the direction of parents in the cascade...
	href?: string;
	nowrap?: boolean;
	font?: string;
	debug?: boolean;
	accessor?: keyof typeof accessors | ( ( value: unknown, accessorArgs?: Record<string, unknown> ) => string | ReactNode );
	accessorArgs?: Record<string, unknown>;
	nullOnFalsyAccessorValue?: boolean;

	/** deprecate this... */
	swatch?: any;
	h1?: boolean;
	h2?: boolean;
	h3?: boolean;
	h4?: boolean;
	h5?: boolean;
	h6?: boolean;

	xxsm?: boolean;
	xsm?: boolean;
	sm?: boolean;
	md?: boolean;
	lg?: boolean;
	xl?: boolean;
	xxl?: boolean;
	xxxl?: boolean;
	span?: boolean;
	visible?: boolean;
}



export const Text = ({
	as,
	className = "",
	color,
	accessor,
	accessorArgs,
	bold,
	indent,
	size,
	margin,
	swatch,
	variant = "p",
	align,
	justify,
	opacity,
	children,
	light,
	dark,
	frame,
	numeric,
	weight,
	style,
	hide,
	lineHeight,
	$flow,
	href,
	title,
	nowrap,
	font,
	debug,
	h1,
	h2,
	h3,
	h4,
	h5,
	h6,
	xxsm,
	xsm,
	sm,
	md,
	lg,
	xl,
	xxl,
	span,
	visible = true,
	nullOnFalsyAccessorValue = false,
}: TextProps) => {

	if(!visible) {
		return null;
	}


	variant = h1 ? "h1" : h2 ? "h2" : h3 ? "h3" : h4 ? "h4" : h5 ? "h5" : h6 ? "h6" : variant;
	size = size ?? (xxsm ? "xxsm" : xsm ? "xsm" : sm ? "sm" : md ? "md" : lg ? "lg" : xl ? "xl" : xxl ? "xxl" : undefined);

	const shouldDiv = !areValidSubNodes(children, debug);
	if(shouldDiv) {
		as = "div";
	}

	// as = as || typeof children !== "string" && typeof children !== "number" ? "div" : span ? "span" : variant;
	const valuestring = typeof accessor === "function" ? accessor(children, accessorArgs ?? {}) : typeof accessor === "string" ? accessors[accessor](children, accessorArgs) : children;
	if(debug) {
		console.log("valuestring", typeof valuestring, valuestring);
	}
	if(nullOnFalsyAccessorValue && (valuestring === "" || valuestring === null || valuestring === undefined || valuestring.toString().startsWith("NaN"))) {
		return <></>;
	}
	align = align === "flex-start" ? "top" : align === "flex-end" ? "bottom" : align;
	justify = justify === "flex-start" ? "left" : justify === "flex-end" ? "right" : justify;
	return (
		<TextNode
			className={[...(className.split(" ")), "text-node"].filter(Boolean).join(" ")}
			href={href}
			as={as || variant}
			title={title}
			$color={color}
			$variant={variant}
			$swatch={swatch}
			$size={size}
			$align={align}
			$weight={weight}
			$numeric={numeric}
			style={style}
			$opacity={opacity}
			$hide={hide}
			$bold={bold}
			$lineHeight={lineHeight}
			$indent={indent}
			$flow={$flow}
			$margin={margin}
			$light={light}
			$dark={dark}
			$frame={frame}
			$nowrap={nowrap}
			$justify={justify}
			$font={font}
			$debug={debug}
		>
			{valuestring}
		</TextNode>
	)
}

const areValidSubNodes = ( nodes: unknown, debug: boolean = false ): boolean => {
	const subs = Array.isArray(nodes) ? nodes : [ nodes ];
	if(debug) {
		console.log("subs", subs);
	}
	return subs.every(node => {
		if( typeof node === "string"
				|| typeof node === "number"
				|| typeof node === "boolean" && node === false
				|| typeof node === "function"
				|| typeof node === "undefined"
		) {
			return true;
		}
		if(typeof node === "object" && node !== null && "props" in node && ["span", "a"].includes(node.props.as)) {
			return true;
		}
		if(typeof node === "object" && node !== null && "type" in node && ["ClickTo", "LinkTo", "Text"].includes(node.type.name)) {
			return true;
		}
		return false;
	});
}
